/** QuickActionCard + QuickActionGroup — the universal SUNTRONIX quick-action visual language (approved Home style: rounded card, icon box, title, subtitle). */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, MoreHorizontal, Pin, PinOff, Settings } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/context/AuthContext";
import { canSee } from "@/constants/roleNav";
import { isCustomerRole } from "@/constants/customerNav";
import { MODULE_ACTIONS } from "./moduleActions";
import { ACTION_TONES, toneFor } from "@/lib/actionTones";
import { AddCustomerModal } from "@/components/app/AddCustomerModal";
import { AddProductModal } from "@/components/app/AddProductModal";

let badgeCache = { at: 0, data: null, promise: null };
export function useQuickBadges(enabled = true) {
  const [b, setB] = useState(badgeCache.data || {});
  useEffect(() => {
    if (!enabled) return;
    if (badgeCache.data && Date.now() - badgeCache.at < 60000) { setB(badgeCache.data); return; }
    badgeCache.promise = badgeCache.promise || api.get("/quick-actions/badges").then(({ data }) => { badgeCache = { at: Date.now(), data, promise: null }; return data; }).catch(() => { badgeCache.promise = null; return {}; });
    badgeCache.promise.then((d) => setB(d || {}));
  }, [enabled]);
  return b;
}

let waCache = null;
async function waConfigured() {
  if (waCache !== null) return waCache;
  try { const { data } = await api.get("/whatsapp/status"); waCache = (data.api_key_status || "").toLowerCase().includes("set") || data.status === "live" || data.status === "LIVE" || !!data.sender_number; } catch { waCache = true; }
  return waCache;
}

// coordinated soft-tinted semantic palette (static class strings so Tailwind keeps them); icon tile carries the stronger accent
export const QA_HUES = [
  { card: "bg-sky-50 border-sky-200 hover:border-sky-400", icon: "bg-sky-600 text-white" },
  { card: "bg-violet-50 border-violet-200 hover:border-violet-400", icon: "bg-violet-600 text-white" },
  { card: "bg-amber-50 border-amber-200 hover:border-amber-400", icon: "bg-amber-500 text-white" },
  { card: "bg-teal-50 border-teal-200 hover:border-teal-400", icon: "bg-teal-600 text-white" },
  { card: "bg-rose-50 border-rose-200 hover:border-rose-400", icon: "bg-rose-600 text-white" },
  { card: "bg-indigo-50 border-indigo-200 hover:border-indigo-400", icon: "bg-indigo-600 text-white" },
  { card: "bg-orange-50 border-orange-200 hover:border-orange-400", icon: "bg-orange-500 text-white" },
  { card: "bg-cyan-50 border-cyan-200 hover:border-cyan-400", icon: "bg-cyan-600 text-white" },
  { card: "bg-lime-50 border-lime-200 hover:border-lime-500", icon: "bg-lime-600 text-white" },
  { card: "bg-fuchsia-50 border-fuchsia-200 hover:border-fuchsia-400", icon: "bg-fuchsia-600 text-white" },
  { card: "bg-blue-50 border-blue-200 hover:border-blue-400", icon: "bg-blue-600 text-white" },
  { card: "bg-slate-50 border-slate-200 hover:border-slate-400", icon: "bg-slate-700 text-white" },
];

// semantic hue per Home action: sales/catalogue violet · orders indigo · customers sky · money amber/orange · service rose · assets slate · logistics teal/cyan/blue · stock lime · messaging green · compliance fuchsia
export const QA_HUE_BY_KEY = {
  "acceptance-inbox": { card: "bg-emerald-50 border-emerald-200 hover:border-emerald-400", icon: "bg-emerald-700 text-white" },
  quotations: { card: "bg-violet-50 border-violet-200 hover:border-violet-400", icon: "bg-violet-600 text-white" },
  command: { card: "bg-indigo-50 border-indigo-200 hover:border-indigo-400", icon: "bg-indigo-600 text-white" },
  "add-product": { card: "bg-violet-50 border-violet-200 hover:border-violet-400", icon: "bg-violet-600 text-white" },
  "add-customer": { card: "bg-sky-50 border-sky-200 hover:border-sky-400", icon: "bg-sky-600 text-white" },
  followup: { card: "bg-amber-50 border-amber-200 hover:border-amber-400", icon: "bg-amber-500 text-white" },
  "invoice-copies": { card: "bg-orange-50 border-orange-200 hover:border-orange-400", icon: "bg-orange-500 text-white" },
  service: { card: "bg-rose-50 border-rose-200 hover:border-rose-400", icon: "bg-rose-600 text-white" },
  assets: { card: "bg-slate-50 border-slate-200 hover:border-slate-400", icon: "bg-slate-700 text-white" },
  "address-verification": { card: "bg-teal-50 border-teal-200 hover:border-teal-400", icon: "bg-teal-600 text-white" },
  "delivery-challans": { card: "bg-cyan-50 border-cyan-200 hover:border-cyan-400", icon: "bg-cyan-600 text-white" },
  "dispatch-tracking": { card: "bg-blue-50 border-blue-200 hover:border-blue-400", icon: "bg-blue-600 text-white" },
  "stock-inventory": { card: "bg-lime-50 border-lime-200 hover:border-lime-500", icon: "bg-lime-600 text-white" },
  "wa-new": { card: "bg-green-50 border-green-200 hover:border-green-400", icon: "bg-green-600 text-white" },
  wa: { card: "bg-emerald-50 border-emerald-200 hover:border-emerald-400", icon: "bg-emerald-700 text-white" },
  kyc: { card: "bg-fuchsia-50 border-fuchsia-200 hover:border-fuchsia-400", icon: "bg-fuchsia-600 text-white" },
};

export function QuickActionCard({ icon: Icon, title, subtitle, onClick, primary = false, badge, disabled = false, loading = false, testid, compact = false, tone, hue }) {
  const base = "group relative text-left rounded-lg border transition-[box-shadow,border-color,transform] duration-150 flex items-center gap-2.5 px-3 " + (compact ? "py-2 min-h-[52px]" : "py-2.5 min-h-[58px]") + " w-full";
  const look = primary
    ? " bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700 hover:shadow-md"
    : hue ? ` ${hue.card} text-slate-900 hover:shadow-md hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-slate-400`
    : " bg-[color:var(--st-surface,#fff)] border-[color:var(--st-border,#e2e8f0)] text-[color:var(--st-text,#0f172a)] hover:border-emerald-400/70 hover:shadow-sm hover:-translate-y-px" + (tone === "wa" ? " border-emerald-200" : "");
  const state = disabled || loading ? " opacity-60 cursor-not-allowed hover:translate-y-0 hover:shadow-none" : " cursor-pointer";
  return (
    <button type="button" onClick={disabled || loading ? undefined : onClick} aria-disabled={disabled || loading} aria-busy={loading} className={base + look + state} data-testid={testid}>
      <span className={"h-8 w-8 rounded-md grid place-items-center shrink-0 " + (primary ? "bg-white/15 text-white" : hue ? `${hue.icon} shadow-sm` : tone === "wa" ? "bg-emerald-600 text-white" : "bg-[color:var(--st-surface-alt,#f1f5f9)] text-[color:var(--st-text,#0f172a)] group-hover:bg-emerald-50 group-hover:text-emerald-700")}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : Icon ? <Icon className="h-4 w-4" strokeWidth={1.9} aria-hidden="true" /> : null}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-medium leading-tight truncate">{title}</span>
        {subtitle && <span className={"block text-[10.5px] leading-tight truncate " + (primary ? "text-white/80" : hue ? "text-slate-600" : "text-[color:var(--st-text-muted,#64748b)]")}>{subtitle}</span>}
      </span>
      {badge ? <span className={"shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums " + (primary ? "bg-white text-emerald-700" : hue ? "bg-white/90 text-slate-900 ring-1 ring-slate-300" : "bg-amber-100 text-amber-800")} data-testid={testid ? `${testid}-badge` : undefined}>{badge}</span> : null}
    </button>
  );
}

/**
 * QuickActionGroup — contextual actions for a module or record.
 * props: module (key in MODULE_ACTIONS) · handlers ({handlerName: fn}) for record contexts · extra (array of action defs) · max (default 6) · title · compact · testid
 */
// Home keeps the approved default card sequence regardless of per-user pins
const FIXED_ORDER = new Set(["home"]);

export function QuickActionGroup({ module, handlers = {}, extra = [], max = 6, title = "Quick actions", compact = false, testid, className = "", hideWhenEmpty = true }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role;
  const internal = !!role && !isCustomerRole(role) && !String(role).startsWith("supplier");
  const badges = useQuickBadges(internal);
  const [pins, setPins] = useState(null);
  const [busyKey, setBusyKey] = useState("");
  const [modal, setModal] = useState(null);
  const tracked = useRef(new Set());
  useEffect(() => { if (!internal) return; api.get("/quick-actions/pins").then(({ data }) => setPins(data.pins || {})).catch(() => setPins({})); }, [internal]);

  const all = useMemo(() => {
    const defs = [...(MODULE_ACTIONS[module] || []), ...extra]
      // role without the primary destination keeps its existing route via an honestly labelled fallback (same key → pins/badges unchanged)
      .map((d) => (d.to && d.fallback && !canSee(role, d.to.split("?")[0]) ? { ...d, ...d.fallback } : d));
    return defs.filter((d) => {
      if (d.handler && !handlers[d.handler]) return false;
      if (d.perm === false) return false;
      if (d.to && !canSee(role, d.to.split("?")[0])) return false;
      if (!d.to && !internal) return false;
      return true;
    });
  }, [module, extra, handlers, role, internal]);

  const fixed = FIXED_ORDER.has(module);
  const ordered = useMemo(() => {
    const pinned = fixed ? [] : (pins && pins[module]) || [];  // fixed modules: registry order wins; saved pins stay stored, untouched
    const primary = all.find((d) => d.primary);
    const rest = all.filter((d) => d !== primary);
    const byPin = [...rest.filter((d) => pinned.includes(d.key)), ...rest.filter((d) => !pinned.includes(d.key))];
    return primary ? [primary, ...byPin] : byPin;
  }, [all, pins, module, fixed]);
  const visible = ordered.slice(0, max);
  const overflow = ordered.slice(max);

  const run = useCallback(async (d) => {
    if (busyKey) return;
    if (!tracked.current.has(d.key)) { tracked.current.add(d.key); api.post("/quick-actions/track", { action: d.key, module, route: d.to || null }).catch(() => {}); }
    if (d.wa && !(await waConfigured())) {
      toast.error("WhatsApp integration requires configuration.", { action: { label: "Open Integration", onClick: () => navigate("/admin/integrations/whatsapp?view=connection") } });
      return;
    }
    try {
      setBusyKey(d.key);
      if (d.handler) await handlers[d.handler](d);
      else if (d.modal) setModal(d.modal);
      else if (d.run) await d.run(d);
      else if (d.to) navigate(d.to);
    } catch (e) { toast.error(e?.response?.data?.detail || e?.message || "Action failed"); }
    finally { setBusyKey(""); }
  }, [busyKey, handlers, module, navigate]);

  const togglePin = async (key) => {
    const cur = (pins && pins[module]) || [];
    const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
    setPins({ ...(pins || {}), [module]: next });
    try { await api.put("/quick-actions/pins", { module, keys: next }); } catch { toast.error("Could not save pin"); }
  };

  if (!all.length) return hideWhenEmpty ? null : <div className="text-[11px] text-slate-500">No actions available for your role.</div>;
  const tid = testid || `qa-${module}`;
  const badgeOf = (d) => (d.badge && badges[d.badge] ? badges[d.badge] : undefined);
  return (
    <section aria-label={title} className={"mb-4 " + className} data-testid={tid}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--st-text-subtle,#94a3b8)] font-semibold">{title}</div>
        {overflow.length > 0 && (
          <Popover>
            <PopoverTrigger asChild><button type="button" className="inline-flex items-center gap-1 text-[11px] text-[color:var(--st-text-muted,#64748b)] hover:text-emerald-700" data-testid={`${tid}-more`}><MoreHorizontal className="h-3.5 w-3.5" />More actions ({overflow.length})</button></PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-2" data-testid={`${tid}-more-panel`}>
              {!fixed && <div className="text-[10px] uppercase tracking-wide text-slate-500 px-1 mb-1 inline-flex items-center gap-1"><Settings className="h-3 w-3" />Pin to quick actions</div>}
              <div className="space-y-1">
                {overflow.map((d) => (
                  <div key={d.key} className="flex items-center gap-1">
                    <div className="flex-1"><QuickActionCard compact icon={d.icon} title={d.title} subtitle={d.subtitle} badge={badgeOf(d)} loading={busyKey === d.key} onClick={() => run(d)} testid={`${tid}-${d.key}`} /></div>
                    {!fixed && <button type="button" title="Pin" onClick={() => togglePin(d.key)} className="p-1 text-slate-400 hover:text-emerald-700" data-testid={`${tid}-pin-${d.key}`}><Pin className="h-3.5 w-3.5" /></button>}
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
      <div className={"grid gap-2 grid-cols-2 sm:grid-cols-3 " + (visible.length >= 6 ? "xl:grid-cols-6" : visible.length === 5 ? "xl:grid-cols-5" : visible.length === 4 ? "xl:grid-cols-4" : "lg:grid-cols-3")}>
        {visible.map((d, i) => (
          <div key={d.key} className="relative group/qa">
            <QuickActionCard compact={compact} icon={d.icon} title={d.title} subtitle={d.subtitle} primary={!!d.primary} tone={d.tone} hue={d.primary ? undefined : module === "home" ? QA_HUE_BY_KEY[d.key] || QA_HUES[i % QA_HUES.length] : ACTION_TONES[d.tone === "wa" ? "comms" : toneFor(d.key)]} badge={badgeOf(d)} loading={busyKey === d.key} onClick={() => run(d)} testid={`${tid}-${d.key}`} />
            {!fixed && !d.primary && pins && (pins[module] || []).includes(d.key) && <button type="button" title="Unpin" onClick={() => togglePin(d.key)} className="absolute -top-1.5 -right-1.5 hidden group-hover/qa:grid h-5 w-5 place-items-center rounded-full bg-white border border-slate-200 text-slate-500 hover:text-rose-600" data-testid={`${tid}-unpin-${d.key}`}><PinOff className="h-3 w-3" /></button>}
          </div>
        ))}
      </div>
      {modal === "add_customer" && <AddCustomerModal onClose={() => setModal(null)} onCreated={(c) => { setModal(null); toast.success(`${c?.customer_code || "Customer"} created`); if (c?.id) navigate(`/commercial/crm/customers/${c.id}`); }} />}
      {modal === "add_product" && <AddProductModal context="Dashboard quick action" returnChoices onClose={() => setModal(null)} onCreated={(p) => { toast.success(`${p?.product_name || "Product"} saved to Product Master`); }} />}
    </section>
  );
}
